/**
 * Individual Activity Booking Client Page
 *
 * Client component that handles i18n translations for activity booking page.
 * Follows the same structure as LodgeBookingClientPage
 */

'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { Adventure } from '@/lib/data';
import ActivityBookingClient from './ActivityBookingClient';
import styles from './page.module.css';

interface Props {
  activity: Adventure;
}

// Activity metadata — uses translation keys instead of hardcoded strings
const activityMetadata: Record<string, {
  statKey: string;
  stats: Array<{ number?: string; numberKey?: string; labelKey: string; descKey: string }>;
}> = {
  'resort-skiing': {
    statKey: 'resortSkiing',
    stats: [
      { number: '6h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '12', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'easy', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '100%', labelKey: 'equipment', descKey: 'equipmentDesc' },
    ],
  },
  'heli-skiing': {
    statKey: 'heliSkiing',
    stats: [
      { number: '8h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '6', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'expert', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '5+', labelKey: 'runs', descKey: 'runsDesc' },
    ],
  },
  'cat-skiing': {
    statKey: 'catSkiing',
    stats: [
      { number: '7h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '12', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'moderate', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '10+', labelKey: 'runs', descKey: 'runsDesc' },
    ],
  },
  'fly-fishing': {
    statKey: 'flyFishing',
    stats: [
      { number: '6h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '4', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'easy', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '100%', labelKey: 'gear', descKey: 'gearDesc' },
    ],
  },
  'ski-touring': {
    statKey: 'skiTouring',
    stats: [
      { number: '7h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '6', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'challenging', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '100%', labelKey: 'safety', descKey: 'safetyDesc' },
    ],
  },
  'water-skiing': {
    statKey: 'waterSkiing',
    stats: [
      { number: '3h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '6', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'easy', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '100%', labelKey: 'equipment', descKey: 'equipmentDesc' },
    ],
  },
};

export default function ActivityBookingClientPage({ activity }: Props) {
  const t = useTranslations('bookingActivity');
  const tActivities = useTranslations('bookingActivities');
  const metadata = activityMetadata[activity.id] ?? {
    statKey: 'resortSkiing',
    stats: [
      { number: '6h', labelKey: 'duration', descKey: 'durationDesc' },
      { number: '12', labelKey: 'maxGroup', descKey: 'groupDesc' },
      { numberKey: 'easy', labelKey: 'difficulty', descKey: 'difficultyDesc' },
      { number: '100%', labelKey: 'equipment', descKey: 'equipmentDesc' },
    ],
  };

  return (
    <div className={styles.bookingPage}>
      {/* Hero Section */}
      <section className={styles.hero} data-hero-background>
        <div className={styles.heroBackground}>
          <Image
            src={activity.heroImage}
            alt={`${activity.title} - ${activity.subtitle || 'Guided Adventure'}`}
            fill
            priority
            sizes="100vw"
            className={styles.heroImage}
          />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.heroContent}>
          <div className={styles.breadcrumb}>
            <a href="/booking" className={styles.breadcrumbLink}>
              {t('breadcrumb.booking')}
            </a>
            <span className={styles.breadcrumbSeparator}>/</span>
            <a href="/booking/activities" className={styles.breadcrumbLink}>
              {t('breadcrumb.activities')}
            </a>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>{activity.title}</span>
          </div>
          <h1 className={styles.heroTitle}>{activity.title}</h1>
          {activity.subtitle && (
            <p className={styles.heroSubtitle}>{activity.subtitle}</p>
          )}
          {activity.location && (
            <div className={styles.heroLocation}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>{activity.location}</span>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            {metadata.stats.map((stat, i) => (
              <div key={i} className={styles.statCard}>
                <div className={styles.statNumber}>
                  {stat.numberKey
                    ? tActivities(`difficulties.${stat.numberKey}`)
                    : stat.number}
                </div>
                <div className={styles.statLabel}>{t(`stats.${stat.labelKey}`)}</div>
                <div className={styles.statDescription}>
                  {t(`activityStats.${metadata.statKey}.${stat.descKey}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section (Client Component) */}
      <ActivityBookingClient activity={activity} />

      {/* Details Section */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            <div className={styles.detailsContent}>
              <h2 className={styles.detailsTitle}>{t('about')}</h2>
              <p className={styles.detailsDescription}>{activity.description}</p>

              {/* What's Included */}
              <div className={styles.detailsBlock}>
                <h3 className={styles.blockTitle}>{t('whatsIncluded')}</h3>
                <ul className={styles.detailsList}>
                  {(['guide', 'equipment', 'safety', 'transport', 'refreshments'] as const).map((key) => (
                    <li key={key} className={styles.detailsItem}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span>{t(`included.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What to Bring */}
              <div className={styles.detailsBlock}>
                <h3 className={styles.blockTitle}>{t('whatToBring')}</h3>
                <ul className={styles.detailsList}>
                  {(['clothing', 'sunscreen', 'water', 'camera'] as const).map((key) => (
                    <li key={key} className={styles.detailsItem}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      <span>{t(`toBring.${key}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Image Gallery Placeholder */}
            <div className={styles.galleryContainer}>
              <div className={styles.gallery}>
                <div className={styles.galleryItem}>
                  <Image
                    src={activity.heroImage}
                    alt={activity.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className={styles.galleryImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
            <p className={styles.ctaDescription}>
              {t('cta.description', { title: activity.title })}
            </p>
            <div className={styles.ctaButtons}>
              <a href="/contact" className={styles.ctaButtonPrimary}>
                {t('cta.contact')}
              </a>
              <a href="/booking/activities" className={styles.ctaButtonSecondary}>
                {t('cta.viewAll')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
